import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api } from '../lib/api'
import { getLocalSkillsEnabled, setLocalSkillsEnabled, getDisabledLocalSkills, setDisabledLocalSkills } from '../lib/agent-prefs'

/**
 * The Claude-only "Enable local skills" setting — moved here from the chat panel's ⓘ info modal
 * (AgentInfoModal) so it lives with the rest of the Claude configuration in Settings → AI agent
 * → Claude, instead of behind an icon in the chat header. Same behavior as before, relocated:
 * master switch + one switch per discovered skill while the master is on.
 */
export function LocalSkillsSettings() {
  const { t } = useLang()
  const [enabled, setEnabledState] = useState(getLocalSkillsEnabled())
  const [disabled, setDisabledState] = useState<string[]>(getDisabledLocalSkills())

  function toggleEnabled(v: boolean): void {
    setEnabledState(v)
    setLocalSkillsEnabled(v)
  }
  function toggleSkill(name: string): void {
    setDisabledState((prev) => {
      const next = prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
      setDisabledLocalSkills(next)
      return next
    })
  }

  const skillsQuery = useQuery({
    queryKey: ['local-skills'],
    queryFn: () => api.localSkills(),
    enabled,
    staleTime: 60_000,
  })
  const skills = skillsQuery.data?.skills ?? []

  return (
    <div className="agentinfo-settings">
      <h3>{t('agent_info_settings')}</h3>
      <div className="agentinfo-row">
        <span id="settings-local-skills-label">{t('agent_local_skills_toggle')}</span>
        <button type="button" role="switch" aria-checked={enabled} aria-labelledby="settings-local-skills-label"
          className={'switch' + (enabled ? ' on' : '')} onClick={() => toggleEnabled(!enabled)}>
          <span className="switch-knob" aria-hidden="true" />
        </button>
      </div>
      {enabled && (
        <div className="agentinfo-skills">
          {skillsQuery.isLoading && <p className="agentinfo-skills-note">{t('loading')}</p>}
          {!skillsQuery.isLoading && skills.length === 0 && <p className="agentinfo-skills-note">{t('agent_local_skills_empty')}</p>}
          {!skillsQuery.isLoading && skills.length > 0 && (
            <ul className="agentinfo-skill-list">
              {skills.map((s, i) => {
                const nameId = `settings-skill-name-${i}`
                const skillOn = !disabled.includes(s.name)
                return (
                  <li key={s.file} className={'agentinfo-skill' + (skillOn ? '' : ' off')}>
                    <div className="agentinfo-skill-head">
                      <div className="agentinfo-skill-title">
                        <b id={nameId}>{s.name}</b>
                        <span className="agentinfo-skill-source">
                          {s.source === 'personal' ? t('agent_local_skills_source_personal') : t('agent_local_skills_source_project')}
                        </span>
                      </div>
                      <button type="button" role="switch" aria-checked={skillOn} aria-labelledby={nameId}
                        className={'switch sm' + (skillOn ? ' on' : '')} onClick={() => toggleSkill(s.name)}>
                        <span className="switch-knob" aria-hidden="true" />
                      </button>
                    </div>
                    <p className="agentinfo-skill-desc">{s.description}</p>
                    <p className="mono agentinfo-skill-file">{s.file}</p>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
